import { getImageAltWithAiIndicator } from '@pkgs/components/utils/getImageAltWithAiIndicator';
import { useCarouselActions } from '@pkgs/components/hooks/useCarouselActions';
import { DEFAULT_IMAGE, getUploadCareImageUrl, ImageAiType } from '@ev-tech/search-modules-api';

import { useDefaultSearchResultCardContext } from './SearchResultCardProvider';
import { SearchResultCardImageLinkWrapper } from './SearchResultCardImageLinkWrapper';
import { Carousel, CarouselImage } from '../Carousel';
import { AiBadge } from '../AiBadge/AiBadge';
import { ViewportSize as viewportSizes } from '../../utils/mediaQueries';
import { getUploadcareImageTransformationProps } from '../../utils/getUploadcareImageTransformationProps';

const MAX_IMAGE_SLIDES = 10;

export const SearchResultCardImageCarousel = () => {
  const {
    carouselPreloadIndex,
    fallbackImage,
    imageComponent,
    link,
    listing,
    shouldHideImage,
    shouldPreloadImage,
    variant,
    translationResolver,
    aiBadgeTranslationKey,
    aiBadgeTranslationResolver,
  } = useDefaultSearchResultCardContext();
  const { uploadCareImages, totalImages } = listing;
  const hasViewMoreSlide = Boolean(totalImages && totalImages > MAX_IMAGE_SLIDES);
  const hasWaterMark = 'hasWaterMark' in listing && listing.hasWaterMark;
  const carouselActions = useCarouselActions();
  const { slidesInView } = carouselActions;
  const aspectRatio = variant === 'preview' ? '16/9' : '3/2';

  const images = uploadCareImages?.length
    ? uploadCareImages.map((image) => ({
        url: getUploadCareImageUrl({ imageId: image.id, useWaterMark: hasWaterMark, isBlurred: false }),
        image,
        focalPoint: image.focalPoint,
        aiType: image.aiType ?? ImageAiType.UNKNOWN,
      }))
    : [{ url: DEFAULT_IMAGE, image: undefined, focalPoint: undefined, aiType: ImageAiType.NONE }];

  return (
    <SearchResultCardImageLinkWrapper>
      <Carousel carouselActions={carouselActions}>
        {images.map((img, carouselIndex) => (
          <CarouselImage
            translationResolver={translationResolver}
            key={carouselIndex}
            aiType={img.aiType}
            aiBadge={
              <AiBadge
                t={aiBadgeTranslationResolver ?? translationResolver}
                translationKey={aiBadgeTranslationKey}
                offset="s"
                size="m"
                aiType={img.aiType}
              />
            }
            carouselIndex={carouselIndex}
            src={img.url}
            sizes={`(max-width: ${viewportSizes.TABLET_PORTRAIT}px) 100vw, (max-width: ${viewportSizes.LAPTOP}px) 50vw, 33vw`}
            imageStyles={{
              borderRadius: 'var(--border-radius-s) var(--border-radius-s) 0 0',
            }}
            focalPoint={img.focalPoint}
            inView={slidesInView.includes(carouselIndex)}
            imageComponent={imageComponent}
            shouldPreloadImage={shouldPreloadImage}
            width={360}
            height={240}
            fallbackImage={fallbackImage}
            aspectRatio={aspectRatio}
            {...getUploadcareImageTransformationProps({ aspectRatio, fit: 'automatic', image: img.image })}
            alt={getImageAltWithAiIndicator({ baseAlt: link.label, imageAiType: img.aiType, t: translationResolver })}
            shouldHideImage={shouldHideImage}
            carouselPreloadIndex={carouselPreloadIndex}
            isViewMoreSlide={carouselIndex === MAX_IMAGE_SLIDES - 1 && hasViewMoreSlide}
          />
        ))}
      </Carousel>
    </SearchResultCardImageLinkWrapper>
  );
};
